import React, { useEffect, useState } from 'react'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Upload, Eye, X } from 'lucide-react'

type Props = {
    label: string
    fieldName: string
    register: UseFormRegister<any>
    errors: any
    existingFile?: string | null
    watchFile?: File | null
    onFileSelect?: (file: File | null) => void
    tooltip?: {
        title?: string
        items: string[]
        position?: 'top' | 'bottom' | 'left' | 'right'
    }
    preview?: string | null
    allowedTypes?: string[]
    fileSizeLimit?: any
    containerClassName?: string,
    required?: boolean,
    longError?: string
}

const FileInputUploadField: React.FC<Props> = ({
    label,
    fieldName,
    register,
    errors,
    existingFile,
    watchFile,
    onFileSelect,
    tooltip,
    preview,
    allowedTypes = [],
    longError,
    fileSizeLimit,
    containerClassName = 'w-full h-[120px] mb-10 m-t-4',
    required = false
}) => {
    const tooltipPositionClass = {
        top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    }

    const [localError, setLocalError] = useState<string | null>(null)
    const [showPreview, setShowPreview] = useState(false)
    const [objectUrl, setObjectUrl] = useState<string | null>(null)

    React.useEffect(() => {
        if (watchFile instanceof File) {
            const url = URL.createObjectURL(watchFile)
            setObjectUrl(url)

            return () => {
                URL.revokeObjectURL(url)
            }
        }

        setObjectUrl(null)
    }, [watchFile])

    // ✅ RESTORED LOGIC
    const displayedPreview = objectUrl || preview || existingFile || null
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null
        setLocalError(null)

        if (!file) return

        if (allowedTypes.length && !allowedTypes.includes(file.type)) {
            setLocalError('Invalid file type')
            e.target.value = ''
            return
        }

        if (fileSizeLimit) {
            const minBytes = fileSizeLimit.min_size * 1024
            const maxBytes = fileSizeLimit.max_size * 1024

            if (file.size < minBytes || file.size > maxBytes) {
                setLocalError(fileSizeLimit.message)
                e.target.value = ''
                return
            }
        }
        setLocalError(null)
        onFileSelect?.(file)
    }

    useEffect(() => {

    }, [errors])

    const clearFile = () => {
        setLocalError(null)
        setObjectUrl(null)

        // Inform parent + RHF
        onFileSelect?.(null)

        // Clear native input
        const input = document.getElementById(fieldName) as HTMLInputElement | null
        if (input) {
            input.value = ''
        }
    }



    return (
        <div id={`${fieldName}__container`} className={`relative ${containerClassName}`}>
            {/* Label */}
            <div className="flex items-center gap-1 mb-2">
                <label
                    className={`text-xs font-medium dark:text-gray-200`}
                        // ${errors[fieldName] ? 'text-red-600' : 'text-gray-700'}
                >
                    {label} 
                </label>
                    {required && <span className="text-red-700">*</span>}
                {tooltip && (
                    <div className="relative group">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                        <div
                            className={`
                                absolute
                                ${tooltipPositionClass[tooltip.position ?? 'top']}
                                w-64 rounded-md bg-white border p-3 text-xs shadow
                                opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                transition z-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700
                            `}
                        >
                            <p className="font-semibold mb-1">{tooltip.title}</p>
                            <ul className="space-y-1">
                                {tooltip.items.map((item, i) => (
                                    <li key={i}>• {item}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* Dashed box */}
            <label
                htmlFor={fieldName}
                className="
                    relative w-full h-full p-2
                    border-2 border-dashed border-gray-300
                    rounded-lg bg-white
                    flex flex-col items-center justify-center
                    cursor-pointer hover:border-blue-400 transition
                    dark:focus:placeholder:text-gray-300
                    dark:bg-slate-700
                    dark:text-white
                    dark:border-gray-600
                    dark:focus:border-blue-500
                    dark:hover:border-blue-500
                "
            >
                {displayedPreview ? (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                        <div className="relative w-full flex-1 bg-gray-50 dark:bg-slate-700 flex items-center justify-center overflow-hidden rounded">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    clearFile()
                                }}
                                className="absolute top-1 right-1 z-10 bg-black/70 hover:bg-black text-white rounded-full p-1"
                            >
                                <X size={14} />
                            </button>

                            <img
                                src={displayedPreview}
                                alt="preview"
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <Upload className="text-gray-400 mb-1" size={20} />
                        <p className="text-xs text-gray-500">Click to upload</p>
                    </>
                )}
            </label>

            <input
                id={fieldName}
                type="file"
                className="hidden"
                accept={allowedTypes.length ? allowedTypes.join(',') : 'image/*'}
                {...register}
                onChange={handleChange}
            />

            <div className='flex item-center'>
                {errors[fieldName] && <p className="text-red-500 text-xs mt-1 mr-2">{errors[fieldName]?.message}.</p>}
                {localError && (
                    <p className="text-red-500 text-xs mt-1">{localError}.</p>
                )}
                {longError && (
                    <p className="text-red-500 text-xs mt-1">{longError}.</p>
                )}
            </div>

            {showPreview && displayedPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="relative bg-white p-3 rounded-lg">
                        <button
                            type="button"
                            onClick={() => setShowPreview(false)}
                            className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1"
                        >
                            <X size={16} />
                        </button>
                        <img
                            src={displayedPreview}
                            alt="Preview"
                            className="max-h-[70vh] rounded"
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default FileInputUploadField
